import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { useWorkingScope, type WorkingScopeSearch } from "../lib/workingScope";
import { Button, EmptyState, Inline, InlineError, Page, useToast } from "../ui";
import { AlertRuleAuthoringForm } from "./alerts/AlertRuleAuthoringForm";
import { AlertRuleAuthoringLeaveGuard } from "./alerts/AlertRuleAuthoringLeaveGuard";
import { useAlertRuleOptions } from "./alerts/useAlertRuleOptions";

type AlertRuleCreateBody = components["schemas"]["AlertRuleCreate"];
type AlertRulePreviewRequest = components["schemas"]["AlertRulePreviewRequest"];

export function AlertRuleCreate() {
  const { agentId } = useWorkingScope();
  const authority = useCallerAuthority();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isDirty, setIsDirty] = useState(false);
  const options = useAlertRuleOptions();
  const canAuthor =
    authority.isOrgOwner ||
    authority.callerMemberships.some(
      (membership) => membership.member_role !== "viewer",
    );

  const createRule = useMutation({
    mutationFn: async (body: AlertRuleCreateBody) => {
      const { data, error } = await apiClient.POST("/v1/alert-rules", {
        body,
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (rule) => {
      queryClient.setQueryData(queryKeys.alertRules.detail(rule.id), rule);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertRules.all,
      });
      toast.success("Alert rule created");
    },
  });

  const previewRule = useMutation({
    mutationFn: async (body: AlertRulePreviewRequest) => {
      const { data, error } = await apiClient.POST("/v1/alert-rules:preview", {
        body,
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  function returnToRules() {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({
        agent,
        tab: "rules" as const,
        team,
      }),
      to: "/alerts",
    });
  }

  if (authority.isError) {
    return (
      <Page variant="config">
        <Inline gap={3}>
          <InlineError>{authority.errorMessage}</InlineError>
          <Button onClick={authority.retry} type="button" variant="secondary">
            Retry permissions
          </Button>
        </Inline>
      </Page>
    );
  }

  if (!authority.isPending && !canAuthor) {
    return (
      <Page variant="config">
        <EmptyState
          description="Requires a member, admin, or owner role."
          title="Alert rule authoring is unavailable"
        />
      </Page>
    );
  }

  return (
    <Page variant="config">
      <AlertRuleAuthoringLeaveGuard isDirty={isDirty} />
      <AlertRuleAuthoringForm
        actionsPlacement="header"
        agentId={agentId}
        catalog={
          options.me?.alert_message_catalog ?? { presets: [], tokens: [] }
        }
        channels={options.channels}
        entityOptions={{
          agents: options.agents,
          agentVersions: options.agentVersions,
          batches: options.batches,
          markers: options.markers,
          templates: options.templates,
        }}
        errorMessage={
          createRule.isError
            ? requestErrorDescription(
                createRule.error,
                "Alert rule creation failed.",
              )
            : undefined
        }
        isOptionsPending={options.isPending || authority.isPending}
        isPending={createRule.isPending}
        isPreviewPending={previewRule.isPending}
        onCancel={returnToRules}
        onDirtyChange={setIsDirty}
        onPreview={async (body) => previewRule.mutateAsync(body)}
        onRetryOptions={options.retry}
        onSubmit={async (body) => {
          await createRule.mutateAsync(body);
          setIsDirty(false);
          returnToRules();
        }}
        optionsErrorMessage={options.errorMessage}
        preview={previewRule.data}
        previewErrorMessage={
          previewRule.isError
            ? requestErrorDescription(
                previewRule.error,
                "Alert backtest failed.",
              )
            : undefined
        }
      />
    </Page>
  );
}
